import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, BookOpen, Clock, Users, Zap, ArrowRight, ArrowLeft, Target, Sparkles, GraduationCap, RefreshCw } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "cramming-feels-productive", title: "Why Cramming Feels Productive" },
  { id: "spaced-learning", title: "Pomodoro Supports Spaced Learning" },
  { id: "structure-exam-prep", title: "How to Structure Exam Prep" },
  { id: "overwhelming-material", title: "Handling Overwhelming Material" },
  { id: "active-recall", title: "Using Pomodoro With Active Recall" },
  { id: "reduce-burnout", title: "Reducing Exam-Season Burnout" },
  { id: "studying-with-others", title: "Studying for Exams With Others" },
  { id: "csw-exams", title: "Where CSW Fits In" },
  { id: "faq", title: "FAQ" },
];

const ExamPrep = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why doesn't cramming work for exams?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Cramming feels productive because you're covering a lot at once, but research consistently shows that massed studying (cramming) leads to weaker long-term retention than spaced studying. What you remember the night before often fades by test time."
        }
      },
      {
        "@type": "Question",
        "name": "How does Pomodoro support spaced learning?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pomodoro naturally breaks study time into small, repeated units across days. Instead of one long cram session, you complete multiple short sessions — which is exactly what spacing research recommends for better retention."
        }
      },
      {
        "@type": "Question",
        "name": "How should I structure exam prep with Pomodoro?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Structure your prep by rotating subjects per Pomodoro block, interleaving topics within subjects, reviewing weakest areas during high-focus times, and using longer breaks between subject changes. This approach improves both learning and problem-solving."
        }
      },
      {
        "@type": "Question",
        "name": "How does Pomodoro help with overwhelming exam material?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pomodoro contains effort by reframing studying as 'I'll cover this topic for 25 minutes' instead of 'I have to learn all of this.' Research shows that bounded tasks reduce anxiety and increase follow-through."
        }
      },
      {
        "@type": "Question",
        "name": "How can I combine Pomodoro with active recall?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Use Pomodoro sessions to test yourself rather than passively re-read. Practice questions, flashcards, and self-quizzing fit naturally into timed focus blocks. Research shows this combination dramatically improves learning outcomes."
        }
      },
      {
        "@type": "Question",
        "name": "How does Pomodoro reduce exam-season burnout?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Exam weeks are exhausting because studying often feels endless. Pomodoro's enforced breaks prevent cognitive overload, reduce stress accumulation, and keep energy levels sustainable over multiple days of studying."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Using Pomodoro for Exam Prep: How to Study Effectively Without Cramming",
    "description": "Learn how to use the Pomodoro Technique for effective exam preparation using spaced learning, active recall, and structured study sessions.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/exam-prep/"
    },
    "keywords": ["pomodoro technique", "exam prep", "study for exams", "spaced learning", "active recall", "exam preparation", "stop cramming", "study technique", "study stream", "online workspace", "virtual study room", "focus timer", "test preparation", "study with me", "online study platform", "interleaving"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Using Pomodoro for Exam Prep: How to Study Effectively Without Cramming | CSW",
    "description": "Learn how to use the Pomodoro Technique for effective exam preparation using spaced learning, active recall, and structured study sessions.",
    "url": "https://csw.live/pomodoro-technique/exam-prep/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Exam Preparation"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Exam Preparation" },
      { "@type": "Thing", "name": "Spaced Learning" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Active Recall" },
      { "@type": "Thing", "name": "Interleaving" },
      { "@type": "Thing", "name": "Test Preparation" },
      { "@type": "Thing", "name": "Cramming Prevention" },
      { "@type": "Thing", "name": "Study Sessions" }
    ],
    "keywords": "pomodoro technique, exam prep, study for exams, spaced learning, active recall, study stream, online workspace, virtual study room, focus timer, test preparation, interleaving"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Exam Prep",
        "item": "https://csw.live/pomodoro-technique/exam-prep/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "Pomodoro + Active Learning",
      path: "/pomodoro-technique/active-learning/",
      description: "How to study smarter, not longer"
    },
    {
      title: "Why Breaks Make You Smarter",
      path: "/pomodoro-technique/why-breaks-matter/",
      description: "The psychology behind Pomodoro breaks"
    },
    {
      title: "Studying Without Burning Out",
      path: "/pomodoro-technique/avoid-burnout/",
      description: "How Pomodoro helps manage stress"
    },
    {
      title: "Building Better Study Habits",
      path: "/pomodoro-technique/study-habits/",
      description: "How Pomodoro creates lasting routines"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Using Pomodoro for Exam Prep: Study Effectively Without Cramming | CSW"
        description="Learn how to use the Pomodoro Technique for effective exam preparation using spaced learning, active recall, and structured study sessions."
        canonicalUrl="/pomodoro-technique/exam-prep/"
      />
      <Header />
      <BlogReadingProgress />
      
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Exam Prep</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <GraduationCap className="w-4 h-4" />
                <span>Exam Preparation</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Using Pomodoro for{" "}
                <span className="text-primary">Exam Prep</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                How to study effectively without cramming.
              </p>
            </div>
          </div>
        </section>

        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              <div className="flex-1 max-w-3xl">
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                  Exam season is high-stakes. Everything feels urgent. And the temptation to cram is strong.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  But cramming doesn't work nearly as well as it feels like it does.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  The Pomodoro Technique offers a better way — structured, paced, and sustainable. Here's how to use it for exam prep.
                </p>

                {/* Why Cramming Feels Productive */}
                <div id="cramming-feels-productive" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Cramming Feels Productive (But Isn't)
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Cramming feels productive because you're covering a lot at once.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  But research consistently shows that <strong>massed studying (cramming) leads to weaker long-term retention</strong> than spaced studying (
                  <a href="https://psycnet.apa.org/record/2006-06949-007" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Cepeda et al., 2006</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  What you remember the night before often fades by test time.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro works differently — by distributing effort across time. This approach also helps you <Link to="/pomodoro-technique/avoid-burnout/" className="text-primary hover:underline">avoid burnout</Link> during intense study periods.
                  </p>
                </div>

                {/* Pomodoro Supports Spaced Learning */}
                <div id="spaced-learning" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Pomodoro Supports Spaced Learning Naturally
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro naturally breaks study time into small, repeated units across days.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Instead of one long cram session, you complete multiple short sessions.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on the spacing effect shows that <strong>distributed practice dramatically improves retention</strong> compared to massed practice (
                  <a href="https://journals.sagepub.com/doi/10.1111/j.1467-9280.2008.02082.x" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Cepeda et al., 2008</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro makes spacing easy.
                  </p>
                </div>

                {/* How to Structure Exam Prep */}
                <div id="structure-exam-prep" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        How to Structure Exam Prep With Pomodoro
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Here's a simple structure students find effective:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Rotate subjects per Pomodoro block</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Interleave topics within subjects</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Review weakest areas during high-focus times</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Use longer breaks between subject changes</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on interleaving shows that <strong>mixing topics improves learning and problem-solving</strong> compared to blocked practice (
                  <a href="https://www.apa.org/pubs/journals/releases/edu-edu0000035.pdf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Rohrer & Taylor, 2007</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro makes rotation manageable.
                  </p>
                </div>

                {/* Handling Overwhelming Material */}
                <div id="overwhelming-material" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Pomodoro Helps With Overwhelming Exam Material
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Exams often cover huge amounts of material. It's easy to feel paralyzed.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro contains that overwhelm by reframing studying as:
                </p>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-8">
                  <p className="text-xl font-display font-semibold text-foreground text-center m-0">
                    "I'll cover this topic for 25 minutes."
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Instead of: "I have to learn all of this."
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on procrastination shows that <strong>bounded tasks reduce anxiety</strong> and increase follow-through (
                  <a href="https://psycnet.apa.org/record/2013-43659-001" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Sirois & Pychyl, 2013</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    You don't need to conquer everything at once.
                  </p>
                </div>

                {/* Using Pomodoro With Active Recall */}
                <div id="active-recall" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Using Pomodoro With Active Recall
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  The most effective exam prep isn't re-reading — it's <strong>testing yourself</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro fits perfectly with active recall:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Use focus blocks for practice questions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Review flashcards within timed sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>Self-quiz during each Pomodoro</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research shows that <strong>retrieval practice produces meaningful learning</strong> (
                  <a href="https://www.pnas.org/doi/10.1073/pnas.1107400108" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Karpicke & Blunt, 2011</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro + active recall = effective exam prep.
                  </p>
                </div>

                {/* Reducing Exam-Season Burnout */}
                <div id="reduce-burnout" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Reducing Exam-Season Burnout
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Exam weeks are exhausting because studying often feels endless.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro's enforced breaks:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>prevent cognitive overload</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>reduce stress accumulation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>keep energy levels sustainable</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on burnout shows that <strong>chronic overload without recovery leads to diminished performance</strong> (
                  <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4911781/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Maslach & Leiter, 2016</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro protects your energy across multiple days of studying.
                  </p>
                </div>

                {/* Studying for Exams With Others */}
                <div id="studying-with-others" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Studying for Exams With Others
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Exam prep doesn't have to be isolating.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro-based study rooms provide:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>co-presence without distraction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>shared rhythm and structure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>gentle accountability</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on peer effects shows that <strong>co-presence increases persistence</strong> (
                  <a href="https://www.journals.uchicago.edu/doi/10.1086/497351" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Falk & Ichino, 2006</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Exam prep feels easier when you're not doing it alone.
                  </p>
                </div>

                {/* Where CSW Fits In */}
                <div id="csw-exams" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Where CSW Fits Into Exam Prep
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is where <strong>csw.live</strong> fits naturally.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  CSW provides:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <Timer className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>Pomodoro-based study sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>shared exam prep environments</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <RefreshCw className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>structured breaks during high-stress periods</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>accountability without pressure</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Instead of cramming alone, CSW gives you a structured environment where focused exam prep is the default.
                </p>

                {/* Final Takeaway */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 mb-12">
                  <h3 className="text-xl font-display font-bold mb-4">Final Takeaway</h3>
                  <p className="text-lg font-body text-foreground/90 mb-0">
                    Exams don't have to mean panic and cramming. Pomodoro gives you a structured, sustainable way to prepare — spacing your learning, protecting your energy, and making progress one session at a time.
                  </p>
                </div>

                {/* FAQ Section */}
                <div id="faq" className="bg-card border border-border rounded-2xl p-8 mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
                        Frequently Asked Questions
                      </h2>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {faqSchema.mainEntity.map((faq, index) => (
                      <div key={index} className="border-b border-border pb-6 last:border-0 last:pb-0">
                        <h3 className="text-lg font-display font-semibold mb-2">{faq.name}</h3>
                        <p className="text-foreground/80 font-body">{faq.acceptedAnswer.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center py-8 border-t border-border">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Pomodoro Technique</span>
                  </Link>
                  <Link 
                    to="/study-timer/" 
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <span>Try the Pomodoro Timer</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Related Articles */}
                <div className="mt-16">
                  <h2 className="text-2xl font-display font-bold mb-8">Related Articles</h2>
                  <div className="grid gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">{article.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Study Stream Resources */}
                <div className="mt-12 p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl">
                  <h2 className="text-2xl font-display font-bold mb-3">Cram Smarter Inside a Study Stream</h2>
                  <p className="text-muted-foreground mb-6">
                    Exam-week Pomodoros become unstoppable when you run them inside a live <Link to="/study-stream/" className="text-primary hover:underline">Study Stream</Link>. Shared timers, silent presence, zero loneliness:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Link to="/study-stream/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Free 24/7 Study Stream for Exam Prep</div>
                      <div className="text-sm text-muted-foreground">Live virtual study rooms with shared Pomodoro timers</div>
                    </Link>
                    <Link to="/study-stream/study-stream-vs-study-group/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Study Stream vs. Study Group for Exams</div>
                      <div className="text-sm text-muted-foreground">Which format actually moves the needle on test prep</div>
                    </Link>
                    <Link to="/study-stream/tech-toolkit-for-success/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Study Stream Tech Toolkit for Exam Success</div>
                      <div className="text-sm text-muted-foreground">Tools and apps that pair with Pomodoro study streams</div>
                    </Link>
                    <Link to="/study-stream/productivity-hacks/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">10 Productivity Hacks from Study Streams</div>
                      <div className="text-sm text-muted-foreground">Battle-tested study stream tactics for exam week</div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default ExamPrep;
